import { Box, Button, Chip } from '@mui/material';
import type { SxProps } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useFragment, graphql } from 'react-relay';
import type { ProjectsFilterType } from './ProjectsFilter.tsx';
import { useProjectBudgetFilterDescription } from './useProjectBudgetFilterDescription.ts';
import { useDefaultProjectsFilter } from './useProjectsFilterState.ts';
import { countActiveFiltersInProjectsFilter } from './countActiveFiltersInProjectsFilter.ts';
import type { ActiveProjectsFilter_questions$key } from './__generated__/ActiveProjectsFilter_questions.graphql.ts';
import type { ActiveProjectsFilter_responseScales$key } from './__generated__/ActiveProjectsFilter_responseScales.graphql.ts';

export type ActiveProjectsFilterProps = {
    filter: ProjectsFilterType;
    includeDates?: boolean;
    onChange(filter: ProjectsFilterType): void;
    questions: ActiveProjectsFilter_questions$key;
    responseScales: ActiveProjectsFilter_responseScales$key;
    sx?: SxProps;
};

export const ActiveProjectsFilter = (props: ActiveProjectsFilterProps) => {
    const { filter, includeDates, onChange, sx } = props;
    const defaultProjectsFilter = useDefaultProjectsFilter();
    const filterCount = countActiveFiltersInProjectsFilter(filter, {
        countDateFilters: includeDates,
    });
    const questions = useFragment(fragments.questions, props.questions);
    const responseScales = useFragment(
        fragments.responseScales,
        props.responseScales,
    );
    const projectBudgetFilterDescription = useProjectBudgetFilterDescription(
        filter.projectBudget,
    );
    const hasFilters = filterCount > 0 || filter.sharedProjects.length > 0;

    if (!hasFilters) {
        return null;
    }
    return (
        <Box
            display="flex"
            gap={0.25}
            flexWrap="wrap"
            justifyContent="flex-end"
            alignItems="center"
            sx={sx}
        >
            {filter.metaResults.map((propFilter) =>
                questions
                    .filter(({ isSurveyMeta }) => isSurveyMeta)
                    .filter(
                        (question) =>
                            question.originalId === propFilter.metaKey,
                    )
                    .map((question) =>
                        responseScales
                            .find(
                                ({ originalId }) =>
                                    originalId === question.responseScaleId,
                            )
                            ?.responseOptions.filter(
                                ({ value }) =>
                                    propFilter.metaValues.indexOf(
                                        value.toString(),
                                    ) !== -1,
                            )
                            .map(({ label, value }, key) => (
                                <Chip
                                    key={key}
                                    label={label}
                                    color="primary"
                                    size="small"
                                    onDelete={() => {
                                        onChange({
                                            ...filter,
                                            metaResults: filter.metaResults
                                                .map((f) => {
                                                    if (
                                                        f.metaKey ===
                                                        propFilter.metaKey
                                                    ) {
                                                        return {
                                                            metaKey:
                                                                propFilter.metaKey,
                                                            metaValues:
                                                                f.metaValues.filter(
                                                                    (v) =>
                                                                        v.toString() !==
                                                                        value.toString(),
                                                                ),
                                                        };
                                                    }
                                                    return f;
                                                })
                                                .filter(
                                                    (f) =>
                                                        f.metaValues.length > 0,
                                                ),
                                        });
                                    }}
                                />
                            )),
                    ),
            )}
            {filter.projects.map((project) => (
                <Chip
                    key={project.value}
                    label={project.label}
                    color="primary"
                    size="small"
                    onDelete={() =>
                        onChange({
                            ...filter,
                            projects: filter.projects.filter(
                                ({ value }) => value !== project.value,
                            ),
                        })
                    }
                />
            ))}
            {filter.projectCategories.map((category) => (
                <Chip
                    key={category.value}
                    label={category.label}
                    color="primary"
                    size="small"
                    onDelete={() =>
                        onChange({
                            ...filter,
                            projectCategories: filter.projectCategories.filter(
                                ({ value }) => value !== category.value,
                            ),
                        })
                    }
                />
            ))}
            {filter.sharedProjects.map((sharedProject) => (
                <Chip
                    key={sharedProject.value}
                    label={sharedProject.label}
                    color="primary"
                    size="small"
                    onDelete={() =>
                        onChange({
                            ...filter,
                            sharedProjects: filter.sharedProjects.filter(
                                ({ value }) => value !== sharedProject.value,
                            ),
                        })
                    }
                />
            ))}

            {projectBudgetFilterDescription !== '' && (
                <Chip
                    label={projectBudgetFilterDescription}
                    color="primary"
                    size="small"
                    onDelete={() =>
                        onChange({
                            ...filter,
                            projectBudget: {
                                ...filter.projectBudget,
                                from: null,
                                to: null,
                            },
                        })
                    }
                />
            )}
            <Button
                variant={'text'}
                size="small"
                onClick={() =>
                    onChange(
                        includeDates
                            ? defaultProjectsFilter
                            : {
                                  ...defaultProjectsFilter,
                                  fromDate: filter.fromDate,
                                  toDate: filter.toDate,
                              },
                    )
                }
            >
                <FormattedMessage defaultMessage="Clear" />
            </Button>
        </Box>
    );
};

const fragments = {
    questions: graphql`
        fragment ActiveProjectsFilter_questions on Question
        @relay(plural: true) {
            originalId
            responseScaleId: originalResponseScaleId
            isSurveyMeta
        }
    `,
    responseScales: graphql`
        fragment ActiveProjectsFilter_responseScales on ResponseScale
        @relay(plural: true) {
            originalId
            responseOptions {
                label
                value
            }
        }
    `,
};
